// Summary: Printable KDS ticket view used by browser print and preview flows.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
import React from 'react';
import type { OrderlineSelectionGroup } from '../../../../utils/orderline-selections';
import classes from '../../styles/kds-print-ticket.module.scss';

// Summary: Render model for each line printed on a KDS ticket.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
export type KdsPrintTicketLine = {
  id: number;
  productName: string;
  selectionLabels?: string[];
  selectionGroups?: OrderlineSelectionGroup[];
  quantity: number;
  note?: string | null;
};

// Summary: Localized copy bundle for KDS print ticket chrome.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
type KdsPrintTicketLabels = {
  newBadge: string;
  orderNumber: string;
  time: string;
  waiter: string;
  customer: string;
  diners: string;
  placeholder: string;
  table: string;
  diningType: string;
};

// Summary: Printable ticket props used by preview modal and print execution.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
export type KdsPrintTicketProps = {
  printerName: string;
  tableNumber: string;
  orderNumber: string;
  dinersCount: number;
  printTime: Date;
  waiterName?: string | null;
  guestName?: string | null;
  locale: 'he-IL' | 'en-US';
  labels: KdsPrintTicketLabels;
  orderlines: KdsPrintTicketLine[];
  receiptPaperRef?: React.Ref<HTMLDivElement>;
  isFlat?: boolean;
  // Take-away tickets show the TA code alone (no שולחן label) + the dining-type label.
  isTakeAway?: boolean;
  // Tenant layout flag: table number printed at the bottom, under the dining-type line.
  tableNumberAtBottom?: boolean;
};

// Summary: Receipt-like ticket renderer for on-screen preview and browser print.
// Details: docs/function-library/web/contexts-pages.md#pages-kdspage
export const KdsPrintTicket: React.FC<KdsPrintTicketProps> = ({
  printerName,
  tableNumber,
  orderNumber,
  dinersCount,
  printTime,
  waiterName,
  guestName,
  locale,
  labels,
  orderlines,
  receiptPaperRef,
  isFlat = false,
  isTakeAway = false,
  tableNumberAtBottom = false,
}) => {
  // Tableless dine-in keeps the top layout, matching backend - never a 'שולחן ?' bottom line.
  const hasTable = tableNumber.trim() !== '' && tableNumber !== '?';
  const showTableAtBottom = tableNumberAtBottom && !isTakeAway && hasTable;
  const tableRow = (
    <div
      className={showTableAtBottom ? `${classes.receiptTable} ${classes.receiptTableBottom}` : classes.receiptTable}
      data-testid={showTableAtBottom ? 'kds-print-preview-table-bottom' : 'kds-print-preview-table'}
    >
      {!isTakeAway && (
        <>
          <span className={classes.receiptTableLabel}>{labels.table}</span>
          {' '}
        </>
      )}
      <span className={classes.receiptTableNumber}>{tableNumber}</span>
    </div>
  );

  return (
    <div className={`${classes.receiptPreview} ${isFlat ? classes.flat : ''}`}>
      <div
        className={`${classes.receiptPaper} ${isFlat ? classes.flatPaper : ''}`}
        ref={receiptPaperRef}
        data-testid="kds-print-ticket-receipt-paper"
        data-kds-print-capture="true"
      >
        {!showTableAtBottom && tableRow}
        <div className={classes.receiptHeader}>
          <div className={classes.receiptNewBadge}>{labels.newBadge}</div>
          <div className={classes.receiptPrinterName} data-testid="kds-print-preview-printer-name">{printerName}</div>
        </div>

        <div className={classes.receiptMetaGrid}>
          <div className={classes.receiptMetaRow}>
            <div className={classes.receiptMetaCell}>
              <span className={classes.receiptMetaLabel}>{`${labels.orderNumber}:`}</span>
              <span className={classes.receiptMetaValue} data-testid="kds-print-preview-order-number">{orderNumber}</span>
            </div>
            <div className={classes.receiptMetaCell}>
              <span className={classes.receiptMetaLabel}>{`${labels.time}:`}</span>
              <span className={classes.receiptMetaValue} data-testid="kds-print-preview-time">
                {printTime.toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
          <div className={classes.receiptMetaRow}>
            <div className={classes.receiptMetaCell}>
              <span className={classes.receiptMetaLabel}>{`${labels.waiter}:`}</span>
              <span className={classes.receiptMetaValue} data-testid="kds-print-preview-waiter">{waiterName ?? labels.placeholder}</span>
            </div>
            <div className={classes.receiptMetaCell}>
              <span className={classes.receiptMetaLabel}>{`${labels.diners}:`}</span>
              <span className={classes.receiptMetaValue}>{dinersCount}</span>
            </div>
          </div>
          {guestName && guestName.trim().length > 0 ? (
            <div className={classes.receiptMetaRow}>
              <div className={classes.receiptMetaCell}>
                <span className={classes.receiptMetaLabel}>{`${labels.customer}:`}</span>
                <span className={classes.receiptMetaValue} data-testid="kds-print-preview-customer">{guestName}</span>
              </div>
              <div className={classes.receiptMetaCell} />
            </div>
          ) : null}
        </div>


        <div className={classes.receiptSeparator} aria-hidden="true" />

        <div className={classes.receiptLines}>
          {orderlines.map((line, index) => {
            const groups = line.selectionGroups ?? [];
            return (
              <div key={line.id} className={classes.receiptLine}>
                <span className={classes.receiptLineName}>{line.productName}</span>
                <span className={classes.receiptLineQty}>{line.quantity}</span>
                {groups.map((group, groupIndex) => (
                  <span key={`${group.groupName}-${groupIndex}`} className={classes.receiptLineSelection} dir="auto">
                    {group.groupName}: {group.options.map((opt) => opt.name).join(', ')}
                  </span>
                ))}
                {line.note && line.note.trim().length > 0 ? (
                  <span className={classes.receiptLineNote}>{line.note}</span>
                ) : null}
                {index < orderlines.length - 1 ? <div className={classes.receiptLineSeparator} aria-hidden="true" /> : null}
              </div>
            );
          })}
        </div>

        <div className={classes.receiptDiningTypePlaceholder} data-testid="kds-print-preview-dining-type">{labels.diningType}</div>
        {showTableAtBottom && tableRow}
      </div>
    </div>
  );
};
